import { UserButton, useUser } from "@clerk/clerk-react";
import type { UserResource } from "@clerk/shared/types";
import { Link } from "@tanstack/react-router";
import logo from "../assets/logo2.svg";
import { useSourceTabEnabled } from "../hooks/useSourceTabEnabled";
import type { CurrentPage, NavItem } from "../lib/header-helpers";
import { buildNavItems } from "../lib/header-helpers";
import { useBillingStatus } from "./BillingStatusContext";
import { IterationCounter } from "./IterationCounter";

interface HeaderProps {
  currentPage?: CurrentPage;
}

function HeaderNav({ navItems }: { navItems: NavItem[] }) {
  return (
    <nav className="flex shrink-0 items-center justify-center gap-3">
      {navItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className={`flex shrink-0 items-center justify-center gap-2.5 rounded-[36px] px-6 py-2 text-header-label no-underline transition-colors ${
            item.isActive
              ? "bg-[#3C7CA5] text-(--text-primary)!"
              : "text-(--text-secondary)! hover:text-(--text-primary)! hover:bg-white/5"
          }`}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

function HeaderUserSection({
  user,
}: {
  user: UserResource | null | undefined;
}) {
  return (
    <div className="flex shrink-0 items-center gap-3 pr-2">
      {user && (
        <span className="text-(--text-secondary) text-header-label whitespace-nowrap">
          Hi, {user.firstName || user.username || "User"}
        </span>
      )}
      <UserButton
        appearance={{
          elements: {
            avatarBox: "w-4 h-4",
            userButtonTrigger:
              "flex items-center justify-center focus:shadow-none",
          },
        }}
        afterSignOutUrl="/sign-in"
      />
    </div>
  );
}

export function Header({ currentPage = "home" }: HeaderProps) {
  const { user } = useUser();
  const { billingStatus, isBillingLoading } = useBillingStatus();
  const sourceTab = useSourceTabEnabled();

  const navItems = buildNavItems(
    currentPage,
    billingStatus,
    isBillingLoading,
    sourceTab.enabled,
  );

  return (
    <header className="flex justify-center px-8 py-6">
      <div className="flex items-center justify-between w-full max-w-[1800px]">
        <div className="flex items-center">
          <Link to="/" className="cursor-pointer">
            <img src={logo} alt="Logo" className="w-52" />
          </Link>
          {/* Hidden on /pricing — a "go to pricing" CTA is redundant there. */}
          {currentPage !== "pricing" && <IterationCounter />}
        </div>

        <div className="fixed right-4 top-4 z-50 flex max-w-[calc(100vw-2rem)] items-center justify-center gap-3 overflow-x-auto rounded-[36px] bg-[#262D34] p-1 sm:right-8 sm:top-6 sm:max-w-[calc(100vw-18rem)] scrollbar-hide">
          <HeaderNav navItems={navItems} />
          <HeaderUserSection user={user} />
        </div>
      </div>
    </header>
  );
}
